import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Grid, 
  Box, 
  Paper,
  Button,
  useMediaQuery,
  useTheme,
  Drawer,
  IconButton,
  Skeleton,
  Card,
  CardContent,
  CardMedia,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Pagination
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { search } from '@api/search';
import ProductCard from '../product/ProductCard';
import SearchSuggestions from './SearchSuggestions';
import CloseIcon from '@mui/icons-material/Close';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import ErrorBoundary from '../error/ErrorBoundary';
import EmbeddedComponentError from '../error/EmbeddedComponentError';
import FacetSidebar, { FacetSidebarSkeleton } from '../search/FacetSidebar';
import { logError } from '@utils/errorLogger';
import Breadcrumb from '../common/Breadcrumb';

function useQuery() {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
}

// Utility function to normalize facets for Fusion/Cloud and Solr formats
function normalizeFacets(facets) {
  if (!facets) return null;
  let facetFields = null;
  if (facets.fields) {
    facetFields = {};
    Object.entries(facets.fields).forEach(([field, arr]) => {
      if (Array.isArray(arr)) {
        const buckets = [];
        for (let i = 0; i < arr.length; i += 2) {
          if (arr[i] !== undefined && arr[i+1] !== undefined) {
            buckets.push({ val: arr[i], count: arr[i+1] });
          }
        }
        facetFields[field] = { buckets };
      }
    });
  } else if (facets.facet_fields) {
    facetFields = {};
    Object.entries(facets.facet_fields).forEach(([field, arr]) => {
      if (Array.isArray(arr)) {
        const buckets = [];
        for (let i = 0; i < arr.length; i += 2) {
          if (arr[i] !== undefined && arr[i+1] !== undefined) {
            buckets.push({ val: arr[i], count: arr[i+1] });
          }
        }
        facetFields[field] = { buckets };
      }
    });
  }
  return facetFields;
}

// Loading skeleton components
function ProductCardSkeleton() {
  // Updated skeleton to match new ProductCard layout
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', boxShadow: 'none', border: '1px solid #eee', borderRadius: 2 }}>
      <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '8px 8px 0 0', bgcolor: '#f9f9f9' }} />
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Skeleton variant="text" height={28} width="80%" sx={{ mb: 1 }} />
        <Skeleton variant="text" height={20} width="60%" sx={{ mb: 1 }} />
        <Skeleton variant="text" height={20} width="40%" sx={{ mt: 2 }} />
        <Skeleton variant="rectangular" height={36} width={120} sx={{ mt: 2, borderRadius: 1 }} />
      </CardContent>
    </Card>
  );
}

function SortSelectorSkeleton({ isMobile = false }) {
  // Updated skeleton to match new SortSelector control
  return (
    <Skeleton 
      variant="rectangular" 
      width={isMobile ? 140 : 180} 
      height={isMobile ? 40 : 56}
      sx={{ borderRadius: 1, bgcolor: '#f9f9f9' }}
    />
  );
}

function ViewToggleSkeleton({ isMobile = false }) {
  // Updated skeleton to match new ViewToggle control
  return (
    <Skeleton 
      variant="rectangular" 
      width={isMobile ? 72 : 88} 
      height={isMobile ? 32 : 40}
      sx={{ borderRadius: 1, bgcolor: '#f9f9f9' }}
    />
  );
}



// Optimized ViewToggle component with immediate state updates
const ViewToggle = React.memo(({ viewMode, onViewChange, isMobile = false }) => {
  const handleViewChange = useCallback((event, newView) => {
    if (newView && newView !== viewMode) {
      onViewChange(newView);
    }
  }, [viewMode, onViewChange]);

  return (
    <ToggleButtonGroup
      value={viewMode}
      exclusive
      onChange={handleViewChange}
      size={isMobile ? 'small' : 'medium'}
      sx={{
        '& .MuiToggleButton-root': {
          border: '1px solid',
          borderColor: 'divider',
          minWidth: isMobile ? 32 : 40,
          padding: isMobile ? '4px' : '8px',
          transition: 'all 0.15s ease-in-out',
          '&.Mui-selected': {
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark'
            }
          },
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }
      }}
    >
      <ToggleButton value='grid' aria-label='grid view'>
        <GridViewIcon fontSize={isMobile ? 'small' : 'medium'} />
      </ToggleButton>
      <ToggleButton value='list' aria-label='list view'>
        <ViewListIcon fontSize={isMobile ? 'small' : 'medium'} />
      </ToggleButton>
    </ToggleButtonGroup>
  );
});

ViewToggle.displayName = 'ViewToggle';

// List view product card component
const ProductListCard = React.memo(({ product }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  if (!product || !product.id) {
    return (
      <Card sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" color="error">
          Product information unavailable
        </Typography>
      </Card>
    );
  }

  const productTitle = product.title || product.name || 'Unnamed Product';
  const mainImage = product.image_url_s || product.image_url || product.image || 
    `https://placehold.co/120x120/e9e9e9/969696?text=${encodeURIComponent(productTitle)}`;

  // Helper function to format price
  const formatPrice = (price, currency = 'USD', decimalPlaces = 2) => {
    if (!price || isNaN(price)) return null;
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      }).format(price);
    } catch (error) {
      return `$${Number(price).toFixed(decimalPlaces)}`;
    }
  };

  return (
    <Card 
      component={Link}
      to={`/product/${product.id}`}
      sx={{ 
        p: 2, 
        mb: 2, 
        display: 'flex', 
        alignItems: 'center',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'box-shadow 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }
      }}
    >
      {/* Product Image */}
      <Box sx={{ minWidth: isMobile ? 80 : 120, mr: 2 }}>
        <CardMedia
          component='img'
          height={isMobile ? 80 : 120}
          width={isMobile ? 80 : 120}
          image={mainImage}
          alt={productTitle}
          sx={{
            objectFit: 'contain',
            background: '#f9f9f9',
            borderRadius: 1
          }}
        />
      </Box>

      {/* Product Details */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Typography
          variant={isMobile ? 'subtitle2' : 'h6'}
          sx={{
            fontWeight: 600,
            mb: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {productTitle}
        </Typography>

        {product.manufacturer && (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mb: 1 }}
          >
            {product.manufacturer}
          </Typography>
        )}

        <Typography
          variant='body2'
          color='text.secondary'
          sx={{
            mb: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: isMobile ? 2 : 3,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {product.description || 'No description available'}
        </Typography>

        {/* Price and Actions Row */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            sx={{
              fontWeight: 700,
              color: 'primary.main'
            }}
          >
            {formatPrice(product.price, 'USD', 2) || 'Price unavailable'}
          </Typography>

          <Button
            variant='contained'
            size={isMobile ? 'small' : 'medium'}
            startIcon={<ShoppingCartIcon />}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Add to cart logic would go here
            }}
            sx={{
              backgroundColor: '#C99063',
              '&:hover': {
                backgroundColor: 'primary.dark'
              }
            }}
          >
            Add to Cart
          </Button>
        </Box>
      </Box>
    </Card>
  );
});

ProductListCard.displayName = 'ProductListCard';

// Optimized ProductGrid component
const ProductGrid = React.memo(({ results, loading, resultsPerPage, viewMode = 'grid' }) => {
  if (loading) {
    if (viewMode === 'list') {
      return (
        <Box>
          {Array.from({ length: resultsPerPage }).map((_, index) => (
            <Skeleton
              key={`list-skeleton-${index}`}
              variant='rectangular'
              height={140}
              sx={{ mb: 2, borderRadius: 1 }}
            />
          ))}
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {Array.from({ length: resultsPerPage }).map((_, index) => (
          <Grid item key={`skeleton-${index}`} xs={6} sm={6} md={4} lg={4}>
            <ProductCardSkeleton />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (viewMode === 'list') {
    return (
      <Box>
        {results.map((product, index) => (
          <ErrorBoundary
            key={product.id ? String(product.id) : `product-${index}`}
            componentName={`SearchResultProductList-${product.id || index}`}
            onError={(error, info) => logError(error, { 
              componentName: 'SearchResultProductListCard', 
              productId: product.id,
              title: product.title,
              componentStack: info?.componentStack 
            })}
            fallbackComponent={props => (
              <EmbeddedComponentError 
                {...props} 
                title='Product Display Error' 
                message='This product could not be displayed properly.' 
                minHeight='120px'
                variant='info'
              />
            )}
          >
            <ProductListCard product={product} />
          </ErrorBoundary>
        ))}
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {results.map((product, index) => (
        <Grid item key={product.id ? String(product.id) : `product-${index}`} xs={6} sm={6} md={4} lg={4}>
          <ErrorBoundary
            componentName={`SearchResultProduct-${product.id || index}`}
            onError={(error, info) => logError(error, { 
              componentName: 'SearchResultProductCard', 
              productId: product.id,
              title: product.title,
              componentStack: info?.componentStack 
            })}
            fallbackComponent={props => (
              <EmbeddedComponentError 
                {...props} 
                title='Product Display Error' 
                message='This product could not be displayed properly.' 
                minHeight='300px'
                variant='info'
              />
            )}
          >
            <ProductCard product={product} />
          </ErrorBoundary>
        </Grid>
      ))}
    </Grid>
  );
});

// Sort options with proper Lucidworks Fusion syntax
const SORT_OPTIONS = [
  { value: 'score desc', label: 'Relevance (Best Match)', field: 'score', direction: 'desc' },
  { value: 'title asc', label: 'Title (A-Z)', field: 'title', direction: 'asc' },
  { value: 'title desc', label: 'Title (Z-A)', field: 'title', direction: 'desc' },
  { value: 'price asc', label: 'Price (Low to High)', field: 'price', direction: 'asc' },
  { value: 'price desc', label: 'Price (High to Low)', field: 'price', direction: 'desc' }
];

// Memoized sort selector component
const SortSelector = React.memo(({ sortValue, onSortChange, isMobile = false }) => (
  <FormControl 
    size={isMobile ? 'small' : 'medium'} 
    sx={{ 
      minWidth: isMobile ? 140 : 180,
      '& .MuiSelect-select': {
        fontSize: isMobile ? '0.875rem' : '1rem'
      }
    }}
  >
    <InputLabel id='sort-select-label'>Sort by</InputLabel>
    <Select
      labelId='sort-select-label'
      value={sortValue}
      label='Sort by'
      onChange={(e) => onSortChange(e.target.value)}
      sx={{
        backgroundColor: 'background.paper',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'divider'
        }
      }}
    >
      {SORT_OPTIONS.map(option => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
));

SortSelector.displayName = 'SortSelector';

export default function SearchResults() {
  const query = useQuery().get('q') || '';
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const abortControllerRef = useRef(null);
  
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [facets, setFacets] = useState(null);
  const [selectedFacets, setSelectedFacets] = useState({});
  const [page, setPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sortValue, setSortValue] = useState('score desc'); // Default to relevance
  const resultsPerPage = 12;

  // Separate state for immediate UI updates
  const [immediateViewMode, setImmediateViewMode] = useState('grid');

  // Memoized page parsing
  const currentPage = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get('page'), 10);
    return !isNaN(pageParam) && pageParam > 0 ? pageParam : 1;
  }, [location.search]);

  // Memoized sort parsing from URL
  const currentSort = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const sortParam = params.get('sort');
    return sortParam && SORT_OPTIONS.some(opt => opt.value === sortParam) 
      ? sortParam 
      : 'score desc';
  }, [location.search]);

  // Memoized view parsing from URL
  const currentView = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const viewParam = params.get('view');
    return viewParam === 'list' ? 'list' : 'grid';
  }, [location.search]);

  // Update both immediate and URL state when URL changes
  useEffect(() => {
    setPage(currentPage);
    setSortValue(currentSort);
    setImmediateViewMode(currentView); // Update immediate state too
  }, [currentPage, currentSort, currentView]);

  // Memoized facets parsing
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const facetsParam = params.get('facets');
    
    if (facetsParam) {
      const facetObj = {};
      facetsParam.split('|').forEach(f => {
        const [field, vals] = f.split(':');
        if (field && vals) {
          facetObj[field] = vals.split('~');
        }
      });
      setSelectedFacets(facetObj);
    } else {
      setSelectedFacets({});
    }
  }, [location.search]);

  // Extract category from URL parameters
  const categoryParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('category');
  }, [location.search]);

  // Extract manufacturer from URL parameters
  const manufacturerParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('manufacturer');
  }, [location.search]);

  // Memoized filter queries - now includes category and manufacturer filters
  const filterQueries = useMemo(() => {
    const facetFilters = Object.entries(selectedFacets)
      .flatMap(([field, values]) => (values && values.length > 0 ? values.map(val => `${field}:"${val}"`) : []));
    
    // Add category filter if present in URL
    if (categoryParam) {
      facetFilters.push(`category:"${categoryParam}"`);
    }
    
    // Add manufacturer filter if present in URL
    if (manufacturerParam) {
      facetFilters.push(`manufacturer:"${manufacturerParam}"`);
    }
    
    return facetFilters;
  }, [selectedFacets, categoryParam, manufacturerParam]);

  // Optimized sort change handler
  const handleSortChange = useCallback((newSortValue) => {
    const params = new URLSearchParams(location.search);
    
    if (newSortValue === 'score desc') {
      // Remove sort param for default relevance sort
      params.delete('sort');
    } else {
      params.set('sort', newSortValue);
    }
    
    // Reset to first page when changing sort
    params.set('page', '1');
    
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [location, navigate]);

  // Highly optimized view change handler with immediate updates
  const handleViewChange = useCallback((newViewMode) => {
    // Immediate state update for instant UI feedback
    setImmediateViewMode(newViewMode);
    
    // Batch the URL update to avoid blocking the UI
    requestAnimationFrame(() => {
      const params = new URLSearchParams(location.search);
      
      if (newViewMode === 'grid') {
        params.delete('view');
      } else {
        params.set('view', newViewMode);
      }
      
      // Use replace to avoid history pollution
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    });
  }, [location, navigate]);

  // Optimized search effect with sorting
  useEffect(() => {
    if (!query) return;

    const performSearch = async () => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      
      setLoading(true);
      setError(null);
      
      try {
        const searchParams = {
          query: query || '*:*',
          start: (page - 1) * resultsPerPage,
          rows: resultsPerPage,
          filterQueries: filterQueries,
          facet: true,
          facetLimit: 50,
          facetMinCount: 1,
          queryProfile: 'default',
          fields: 'id,title,price,price_d,price_f,image_url_s,image_url,description,manufacturer,category,sku,variant_images,variants,total_variants,group',
          sort: sortValue, // Add sort parameter
          collapse: true,
          collapseField: 'group',
          expand: true,
          expandField: 'image_url',
          signal
        };
        
        const result = await search(searchParams);
        
        if (signal.aborted) return;
        
        if (result.success) {
          // Process results to include variant images like FeaturedProducts
          const expanded = result.data?.expanded || {};
          
          const productsWithVariants = (result.documents || []).map(product => {
            const groupId = product.group || product.id;
            const expandedDocs = expanded[groupId]?.docs || [];
            
            // Collect variant data with both image URLs and SKUs from expanded docs
            let variantData = expandedDocs
              .filter(doc => doc.image_url)
              .map(doc => ({
                image_url: doc.image_url,
                sku: doc.sku || doc.id,
                title: doc.title,
                id: doc.id,
                product_id: doc.product_id,
              }));
            
            // Collect all unique image URLs from expanded docs
            let variantImages = expandedDocs
              .map(doc => doc.image_url)
              .filter(Boolean);
            
            // Fallback: If no expanded docs and we have a group, search for other products in the same group
            if (variantImages.length === 0 && product.group && result.documents) {
              const sameGroupProducts = result.documents.filter(p => p.group === groupId && p.id !== product.id);
              variantImages = sameGroupProducts
                .map(p => p.image_url)
                .filter(Boolean);
              
              // Also collect variant data for fallback
              variantData = sameGroupProducts
                .filter(p => p.image_url)
                .map(p => ({
                  image_url: p.image_url,
                  sku: p.sku || p.id,
                  title: p.title,
                  id: p.id,
                  product_id: p.product_id,
                }));
            }
            
            // Always include the main product as first variant if not already included
            const mainVariant = {
              image_url: product.image_url,
              sku: product.sku || product.id,
              title: product.title,
              id: product.id,
              product_id: product.product_id,
            };
            
            if (product.image_url && !variantImages.includes(product.image_url)) {
              variantImages.unshift(product.image_url);
              variantData.unshift(mainVariant);
            }
            
            return {
              ...product,
              variant_images: variantImages,
              variant_data: variantData,
              total_variants: variantImages.length
            };
          });
          
          setResults(productsWithVariants);
          setTotalResults(result.totalResults || 0);
          setFacets(normalizeFacets(result.facets) || null);
        } else {
          setError(result.error || 'No results found.');
        }
      } catch (error) {
        if (!signal.aborted) {
          setError(error.message || 'An error occurred while searching');
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };

    // Debounce search calls
    const timeoutId = setTimeout(performSearch, 150);
    
    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [query, page, filterQueries, sortValue]); // Add sortValue to dependencies

  // Optimized facet change handler
  const handleFacetChange = useCallback((field, value) => {
    const current = selectedFacets[field] || [];
    const newSelected = current.includes(value) 
      ? current.filter(v => v !== value)
      : [...current, value];
    
    const updatedFacets = { ...selectedFacets, [field]: newSelected };
    
    // Remove empty arrays
    Object.keys(updatedFacets).forEach(k => {
      if (!updatedFacets[k] || updatedFacets[k].length === 0) {
        delete updatedFacets[k];
      }
    });
    
    const facetsParam = Object.entries(updatedFacets)
      .map(([f, vals]) => `${f}:${vals.join('~')}`)
      .join('|');
    
    const params = new URLSearchParams(location.search);
    if (facetsParam) {
      params.set('facets', facetsParam);
    } else {
      params.delete('facets');
    }
    params.set('page', '1');
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [selectedFacets, location, navigate]);

  // Optimized page change handler
  const handlePageChange = useCallback((event, value) => {
    const params = new URLSearchParams(location.search);
    params.set('page', value);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [location, navigate]);

  // Optimized clear filters handler - preserve category and manufacturer if present
  const handleClearFilters = useCallback(() => {
    const params = new URLSearchParams();
    params.set('q', query);
    
    // Preserve category parameter when clearing other filters
    if (categoryParam) {
      params.set('category', categoryParam);
    }
    
    // Preserve manufacturer parameter when clearing other filters
    if (manufacturerParam) {
      params.set('manufacturer', manufacturerParam);
    }
    
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    setMobileFiltersOpen(false);
  }, [query, categoryParam, manufacturerParam, location.pathname, navigate]);

  // Memoized search suggestions container
  const MemoizedSearchSuggestions = React.memo(({ 
    options, 
    visible, 
    onItemClick, 
    onClose,
    maxItems, 
    maxHeight, 
    containerStyles 
  }) => (
    <SearchSuggestions
      options={options}
      visible={visible}
      onItemClick={onItemClick}
      onClose={onClose}
      maxItems={maxItems}
      maxHeight={maxHeight}
      containerStyles={containerStyles}
    />
  ));

  MemoizedSearchSuggestions.displayName = 'MemoizedSearchSuggestions';

  // Memoized breadcrumb items
  const breadcrumbItems = useMemo(() => {
    const items = [{ label: 'Home', href: '/' }];
    
    if (categoryParam) {
      items.push({ label: categoryParam });
    } else if (manufacturerParam) {
      items.push({ label: manufacturerParam });
    } else if (query && query !== '*:*') {
      items.push({ label: `Search Results for "${query}"` });
    } else {
      items.push({ label: 'All Products' });
    }
    
    return items;
  }, [categoryParam, manufacturerParam, query]);

  return (
    <Container maxWidth='xl' sx={{ py: 4 }}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header with search query, sort, and view toggle */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: 2,
        gap: 2
      }}>
        <Typography variant='h4' sx={{ 
          fontWeight: 700, 
          fontSize: { xs: '1.75rem', md: '2.25rem' },
          mb: { xs: 0, sm: 0 }
        }}>
          {categoryParam 
            ? `${categoryParam} Products${query !== '*:*' ? ` for "${query}"` : ''}`
            : manufacturerParam
              ? `${manufacturerParam} Products${query !== '*:*' ? ` for "${query}"` : ''}`
              : query === '*:*' 
                ? 'Showing All Products' 
                : `Search Results for "${query}"`
          }
        </Typography>
        
        {/* Controls container */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: 2,
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'space-between', sm: 'flex-end' },
          flexWrap: 'wrap'
        }}>
          {!loading && totalResults > 0 && (
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                whiteSpace: 'nowrap'
              }}
            >
              {totalResults.toLocaleString()} result{totalResults !== 1 ? 's' : ''}
            </Typography>
          )}
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {loading ? (
              <>
                <SortSelectorSkeleton isMobile={isMobile} />
                <ViewToggleSkeleton isMobile={isMobile} />
              </>
            ) : (
              <>
                <SortSelector 
                  sortValue={sortValue}
                  onSortChange={handleSortChange}
                  isMobile={isMobile}
                />
                
                <ViewToggle
                  viewMode={immediateViewMode} // Use immediate state for instant feedback
                  onViewChange={handleViewChange}
                  isMobile={isMobile}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
      
      
      {error && <Typography color='error' sx={{ mb: 2 }}>{error}</Typography>}
      {!loading && !error && results.length === 0 && (
        <Typography>No results found.</Typography>
      )}
      
      <Grid container spacing={3} alignItems="flex-start">
        {/* Desktop facet sidebar */}
        {((facets && !isMobile) || (loading && !isMobile)) && (
          <Grid item xs={12} md={3} lg={2.5}>
            <Paper elevation={0} sx={{ 
              p: 0, 
              borderRadius: 0, 
              bgcolor: 'transparent',
              border: '0px solid #eee', 
              position: 'sticky', 
              width: '100%'
            }}>
              {loading ? (
                <FacetSidebarSkeleton />
              ) : (
                <FacetSidebar facets={facets} selectedFacets={selectedFacets} onFacetChange={handleFacetChange} />
              )}
            </Paper>
          </Grid>
        )}
        
        <Grid item xs={12} md={(facets && !isMobile) || (loading && !isMobile) ? 9 : 12} lg={(facets && !isMobile) || (loading && !isMobile) ? 9.5 : 12}>
          <Box sx={{ width: '100%' }}>
            <ProductGrid 
              results={results} 
              loading={loading} 
              resultsPerPage={resultsPerPage}
              viewMode={immediateViewMode} // Use immediate state here too
            />
            
            {/* Pagination */}
            {!loading && totalResults > resultsPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, overflow: 'auto', py: 1 }}>
                <Pagination
                  count={Math.ceil(totalResults / resultsPerPage)}
                  page={page}
                  onChange={handlePageChange}
                  color='primary'
                  shape='rounded'
                  size={isMobile ? 'small' : 'medium'}
                  showFirstButton={!isMobile}
                  showLastButton={!isMobile}
                />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      
      {/* Mobile filters drawer */}
      <Drawer
        anchor="bottom"
        open={isMobile && mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        PaperProps={{ 
          sx: { 
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '90vh'
          } 
        }}
      >
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          borderBottom: '1px solid #eee' 
        }}>
          <Typography variant='h6' sx={{ fontWeight: 600 }}>Filter Results</Typography>
          <IconButton onClick={() => setMobileFiltersOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* Sort and view controls in mobile drawer */}
        <Box sx={{ p: 0, borderBottom: '1px solid #eee' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {loading ? (
              <>
                <SortSelectorSkeleton isMobile={true} />
                <ViewToggleSkeleton isMobile={true} />
              </>
            ) : (
              <>
                <SortSelector 
                  sortValue={sortValue}
                  onSortChange={handleSortChange}
                  isMobile={true}
                />
                
                <ViewToggle
                  viewMode={immediateViewMode} // Use immediate state in drawer too
                  onViewChange={handleViewChange}
                  isMobile={true}
                />
              </>
            )}
          </Box>
        </Box>
        
        <Box sx={{ 
          position: 'relative',
          p: 2, 
          maxHeight: 'calc(90vh - 120px)', 
          overflow: 'auto',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0,0,0,0.05)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '3px',
            '&:hover': {
              background: 'rgba(0,0,0,0.3)',
            }
          },
          '&::before': {
            content: '""',
            position: 'fixed',
            top: 'calc(10vh + 80px)',
            left: 0,
            right: 0,
            height: '12px',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 100%)',
            zIndex: 2,
            pointerEvents: 'none',
            opacity: 0,
            transition: 'opacity 0.2s ease'
          },
          '&::after': {
            content: '""',
            position: 'fixed',
            bottom: 'calc(60px)',
            left: 0,
            right: 0,
            height: '12px',
            background: 'linear-gradient(0deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 100%)',
            zIndex: 2,
            pointerEvents: 'none',
            opacity: 1,
            transition: 'opacity 0.2s ease'
          },
          '&.mobile-scrolled-top::before': {
            opacity: 1
          },
          '&.mobile-scrolled-bottom::after': {
            opacity: 0
          }
        }}
        onScroll={(e) => {
          const { scrollTop, scrollHeight, clientHeight } = e.target;
          e.target.classList.toggle('mobile-scrolled-top', scrollTop > 20);
          e.target.classList.toggle('mobile-scrolled-bottom', scrollTop >= scrollHeight - clientHeight - 20);
        }}
        >
          {loading ? (
            <FacetSidebarSkeleton />
          ) : facets ? (
            <FacetSidebar facets={facets} selectedFacets={selectedFacets} onFacetChange={handleFacetChange} />
          ) : null}
        </Box>
        <Box sx={{ p: 2, borderTop: '1px solid #eee', display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleClearFilters}
            sx={{ flex: 1 }}
            disabled={loading}
          >
            Clear All
          </Button>
          <Button 
            variant="contained" 
            onClick={() => setMobileFiltersOpen(false)}
            sx={{ flex: 1 }}
          >
            Apply Filters
          </Button>
        </Box>
      </Drawer>
    </Container>
  );
}